import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROTULO_PERFIL, type PapelContraparte, type PerfilContraparte } from '@via-tax/contracts'
import { Aviso, Botao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { reaisCompacto } from '../formatos'
import type { RetratoPerfis } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Triagem da carteira (ADR 0032): consulta o cadastro público do CNPJ e separa quem é do Simples (vira
 * pergunta de puro × híbrido), quem é condomínio/órgão público/associação (não aproveita crédito) e quem
 * é do regime regular (crédito integral).
 */

const TOM: Record<PerfilContraparte, 'ok' | 'alerta' | 'navy' | 'neutro'> = {
  REGULAR: 'ok',
  SIMPLES: 'navy',
  INSTITUCIONAL: 'alerta',
  MEI: 'alerta',
  PESSOA_FISICA: 'neutro',
  EXTERIOR: 'neutro',
  INDEFINIDO: 'neutro',
}
const EXPLICACAO: Partial<Record<PerfilContraparte, string>> = {
  SIMPLES: 'falta saber se destacam IBS/CBS em 2027 — é a pergunta a fazer',
  REGULAR: 'crédito integral dos dois lados',
  INSTITUCIONAL: 'não aproveitam crédito, e isso não muda',
  MEI: 'crédito irrisório',
  INDEFINIDO: 'sem consulta, sem cadastro e sem nota emitida por eles',
}

export function TriagemContrapartes({ papel }: { papel: PapelContraparte }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [pedido, setPedido] = useState(false)
  const q = useQuery(trpc.contraparte.perfis.queryOptions({ papel }))
  const triar = useMutation(
    trpc.contraparte.triar.mutationOptions({
      onSuccess: () => {
        setPedido(true)
        setTimeout(() => qc.invalidateQueries({ queryKey: trpc.contraparte.pathKey() }), 20_000)
      },
    }),
  )
  return (
    <PainelTriagem
      papel={papel}
      d={q.data}
      erro={q.isError ? q.error : null}
      pedido={pedido}
      pendente={triar.isPending}
      erroTriar={triar.isError ? triar.error : null}
      pesquisar={() => triar.mutate({ forcar: false })}
    />
  )
}

/** Só a aparência: recebe os dados prontos, para a tela e para a conferência visual. */
export function PainelTriagem({
  papel,
  d,
  erro,
  pedido,
  pendente,
  erroTriar,
  pesquisar,
}: {
  papel: PapelContraparte
  d: RetratoPerfis | undefined
  erro: unknown
  pedido: boolean
  pendente: boolean
  erroTriar: unknown
  pesquisar: () => void
}) {
  const quem = papel === 'FORNECEDOR' ? 'fornecedores' : 'clientes'

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-linha bg-painel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Quem são estes {quem} em 2027</h2>
          <p className="mt-0.5 max-w-3xl text-[12px] text-tinta-2">
            A consulta ao cadastro da Receita separa sozinha os três casos: optante pelo Simples vira pergunta, condomínio/órgão público/associação não aproveita crédito, e o resto
            é regime regular.
          </p>
        </div>
        <Botao onClick={pesquisar} disabled={pendente || pedido}>
          {pedido ? 'consultando…' : 'Pesquisar na Receita'}
        </Botao>
      </div>

      {erroTriar ? <Aviso>{mensagemDeErro(erroTriar)}</Aviso> : null}
      {pedido && <Aviso tom="info">A consulta roda em segundo plano, uma de cada vez para respeitar o limite da fonte. Recarregue daqui a pouco para ver o resultado.</Aviso>}

      {d && (
        <>
          <div className="flex flex-wrap gap-2">
            {d.perfis.map((p) => (
              <div key={p.perfil} className="min-w-[11rem] flex-1 rounded-md bg-fundo p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <Selo tom={TOM[p.perfil as PerfilContraparte]}>{ROTULO_PERFIL[p.perfil as PerfilContraparte]}</Selo>
                  <span className="num text-lg font-semibold">{p.quantidade}</span>
                </div>
                <p className="num mt-1 text-[12px] text-tinta-2">{reaisCompacto(p.valor)} movimentados</p>
                {EXPLICACAO[p.perfil as PerfilContraparte] && <p className="mt-0.5 text-[11px] text-tinta-3">{EXPLICACAO[p.perfil as PerfilContraparte]}</p>}
              </div>
            ))}
          </div>
          <p className="text-[12px] text-tinta-3">
            {d.consultados} de {d.total} já consultados na Receita
            {d.aConsultar > 0 ? ` · faltam ${d.aConsultar}` : ' · carteira completa'}.
          </p>
        </>
      )}
      {erro ? <Aviso>{mensagemDeErro(erro)}</Aviso> : null}
    </section>
  )
}
